import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Settings, Save, DollarSign, Clock, Mail, Flag, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SaaSSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('platform');

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/saas/settings`);
      setSettings(res.data);
    } catch { toast.error('Failed to load settings'); }
    finally { setLoading(false); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/saas/settings`, settings);
      toast.success('Settings saved');
    } catch { toast.error('Failed to save settings'); }
    finally { setSaving(false); }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const updatePlan = (index, field, value) => {
    const plans = [...(settings?.plans || [])];
    plans[index] = { ...plans[index], [field]: field.includes('price') || field === 'seats' ? Number(value) : value };
    updateField('plans', plans);
  };

  const toggleFeatureFlag = (key) => {
    const flags = { ...(settings?.feature_flags || {}) };
    flags[key] = !flags[key];
    updateField('feature_flags', flags);
  };

  if (loading) return <div className="saas-loading">Loading settings...</div>;

  const tabs = [
    { key: 'platform', label: 'Platform', icon: Settings },
    { key: 'plans', label: 'Subscription Plans', icon: DollarSign },
    { key: 'trial', label: 'Trial Defaults', icon: Clock },
    { key: 'features', label: 'Feature Flags', icon: Flag },
  ];

  return (
    <div className="saas-page" data-testid="saas-settings">
      <div className="saas-page-header">
        <div>
          <h2>Platform Settings</h2>
          <p>Configure global platform settings</p>
        </div>
        <button className="btn-primary" onClick={saveSettings} disabled={saving} data-testid="save-settings-btn">
          <Save size={16} />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className="settings-layout">
        <div className="settings-tabs-vertical">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                className={`settings-tab ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
                data-testid={`settings-tab-${tab.key}`}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        <div className="settings-content">
          {activeTab === 'platform' && (
            <div className="settings-section" data-testid="platform-settings">
              <h3>Platform Configuration</h3>
              <div className="settings-form">
                <div className="form-group">
                  <label className="form-label">Platform Name</label>
                  <input className="form-input" value={settings?.platform_name || ''} onChange={(e) => updateField('platform_name', e.target.value)} data-testid="platform-name-input" />
                </div>
                <div className="form-group">
                  <label className="form-label">Support Email</label>
                  <input className="form-input" type="email" value={settings?.support_email || ''} onChange={(e) => updateField('support_email', e.target.value)} data-testid="support-email-input" />
                </div>
                <div className="form-group">
                  <label className="form-label">Default Currency</label>
                  <select className="form-input" value={settings?.default_currency || 'USD'} onChange={(e) => updateField('default_currency', e.target.value)}>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Timezone</label>
                  <select className="form-input" value={settings?.timezone || 'UTC'} onChange={(e) => updateField('timezone', e.target.value)}>
                    <option value="UTC">UTC</option>
                    <option value="US/Eastern">US/Eastern</option>
                    <option value="US/Pacific">US/Pacific</option>
                    <option value="Europe/London">Europe/London</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'plans' && (
            <div className="settings-section" data-testid="plans-settings">
              <h3>Subscription Plans</h3>
              <div className="plans-grid">
                {(settings?.plans || []).map((plan, i) => (
                  <div className="plan-card" key={i}>
                    <div className="plan-card-header">
                      <h4>{plan.name}</h4>
                    </div>
                    <div className="plan-card-body">
                      <div className="form-group">
                        <label className="form-label">Monthly Price ($)</label>
                        <input className="form-input" type="number" value={plan.monthly_price} onChange={(e) => updatePlan(i, 'monthly_price', e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Annual Price ($)</label>
                        <input className="form-input" type="number" value={plan.annual_price} onChange={(e) => updatePlan(i, 'annual_price', e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Seats</label>
                        <input className="form-input" type="number" value={plan.seats} onChange={(e) => updatePlan(i, 'seats', e.target.value)} />
                      </div>
                      <div className="plan-features">
                        {(plan.features || []).map((f, j) => (
                          <span key={j} className="module-tag">{f}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'trial' && (
            <div className="settings-section" data-testid="trial-settings">
              <h3>Trial Defaults</h3>
              <div className="settings-form">
                <div className="form-group">
                  <label className="form-label">Default Trial Duration (days)</label>
                  <input className="form-input" type="number" value={settings?.default_trial_days || 14} onChange={(e) => updateField('default_trial_days', parseInt(e.target.value))} data-testid="trial-days-input" />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'features' && (
            <div className="settings-section" data-testid="features-settings">
              <h3>Feature Flags</h3>
              <div className="feature-flags-list">
                {Object.entries(settings?.feature_flags || {}).map(([key, enabled]) => (
                  <div className="feature-flag-item" key={key}>
                    <div className="flag-info">
                      <strong>{key.replace(/_/g, ' ')}</strong>
                    </div>
                    <button
                      className={`flag-toggle ${enabled ? 'on' : 'off'}`}
                      onClick={() => toggleFeatureFlag(key)}
                      data-testid={`flag-${key}`}
                    >
                      {enabled ? 'ON' : 'OFF'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
